import { onlineManager, useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useCallback } from 'react'
import { toast } from 'sonner'
import type { UpdateBookingTransportationFormFieldSchema } from '@/schemas/booking'
import type { HttpErrorResponse } from '@/types/error'
import type { SubmitHandler } from 'react-hook-form'
import { getFleetsQueryOptions } from '@/queries/query'
import { queryKeys } from '@/queries/keys'
import AddBusModal from '@/components/make-booking/trip-details/ModalAddBus'
import useBookingMutation from '@/hooks/useBookingMutation'
import { isHttpError } from '@/utils/http'
import pluralize from '@/utils/pluralize'

export const Route = createFileRoute('/make-booking/$id/trip-details/add-bus')({
  loader: ({ context }) => {
    if (onlineManager.isOnline() && !context.isRestoring) {
      return context.queryClient.ensureInfiniteQueryData(
        getFleetsQueryOptions(),
      )
    }
    return context.queryClient.getQueryData(queryKeys.fleet.list())
  },
  component: RouteComponent,
})

function RouteComponent() {
  const bookingId = Route.useParams({
    select: (params) => params.id,
  })
  const router = useRouter()

  const { update } = useBookingMutation()
  const { data: resFleets } = useSuspenseInfiniteQuery(getFleetsQueryOptions())

  const fleets = resFleets.pages.flatMap((p) => p.data.transportation_types)

  const onClose = useCallback(() => {
    return router.history.back({ ignoreBlocker: true })
  }, [])

  const onSubmit = useCallback<
    SubmitHandler<UpdateBookingTransportationFormFieldSchema>
  >((data, e) => {
    e?.stopPropagation()
    e?.preventDefault()
    return update.mutateAsync(data, {
      onSuccess: () => {
        toast.success(
          `${pluralize('bus', data.transportations.length, true)} added`,
        )
        return router.history.back({ ignoreBlocker: true })
      },
      onError: async (err) => {
        if (isHttpError(err)) {
          const res = await err.response.json<HttpErrorResponse>()
          return toast.error('message' in res ? res.message : res.error)
        }
        return toast.error('Something went wrong')
      },
    })
  }, [])

  return (
    <AddBusModal
      isOpen={true}
      fleets={fleets}
      bookingId={bookingId}
      onClose={onClose}
      onSubmit={onSubmit}
    />
  )
}
